import React, { useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Button, FormControl, FormLabel, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import useProjects from '../hooks/useProjects';
import { Project } from '../models/project';
import { WorkOrder } from '../models/workOrder';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Props {
  workOrder: WorkOrder | undefined;
  isOpen: boolean;
  onClose: () => void;
}

// item 1353d (Chad: "I still cannot move work orders from one project to another") — the WO ⋮
// "Move to another project…" action. Lists ONLY the same customer's other live projects (the
// server enforces the same rule), works on a locked fully-billed WO, and the WO's invoices move
// with it. A proposal-generated WO is refused by the server — it moves with its proposal.
const WorkOrderMoveModal: React.FC<Props> = ({ workOrder, isOpen, onClose }) => {
  const { data: projects } = useProjects();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState('');
  const [moving, setMoving] = useState(false);

  const candidates = useMemo(() => ((projects?.index ?? []) as Project[])
    .filter((p) => !!workOrder && p.customerId === workOrder.customerId && p.id !== workOrder.projectId && !p.isArchived)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')), [projects, workOrder]);

  const targetName = candidates.find((p) => p.id === targetId)?.name;

  const move = async () => {
    if (!workOrder || !targetId) return;
    setMoving(true);
    try {
      await axios.post(`${apiUrl()}WorkOrder/${workOrder.id}/move`, { projectId: targetId });
      queryClient.invalidateQueries('workOrders');
      queryClient.invalidateQueries('invoices');
      queryClient.invalidateQueries('projects');
      queryClient.invalidateQueries('project');
      toast({
        status: 'success',
        title: `${workOrder.workOrderNumber ?? 'Work order'} moved to ${targetName ?? 'the selected project'}.`,
        description: 'Its invoices moved with it.',
      });
      setTargetId('');
      onClose();
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not move', description: String(e?.response?.data ?? 'Try again.'), duration: 9000, isClosable: true,
      });
    } finally {
      setMoving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Move
          {workOrder?.workOrderNumber ?? 'work order'}
          {' '}
          to another project
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.400" mb={3}>
            Only this customer&apos;s other projects are offered. The work order and every invoice generated from it move together.
          </Text>
          {candidates.length === 0 ? (
            <Alert status="info" fontSize="sm">
              <AlertIcon />
              This customer has no other project to move it to.
            </Alert>
          ) : (
            <FormControl>
              <FormLabel fontSize="sm">New project</FormLabel>
              <Select value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder="Choose a project…">
                {candidates.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={move} isDisabled={!targetId} isLoading={moving}>Move</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WorkOrderMoveModal;
